import { Component } from '@theme/component';
// CartErrorEvent is used for JSDoc type annotations
// eslint-disable-next-line no-unused-vars
import { ThemeEvents, CartErrorEvent } from '@theme/events';

/**
 * @typedef {Object} GiftCardRecipientFormRefs
 * @property {HTMLInputElement} [myEmailButton] - Button for selecting my email option (optional when always-recipient)
 * @property {HTMLInputElement} [recipientEmailButton] - Button for selecting recipient email option (optional when always-recipient)
 * @property {HTMLDivElement} recipientFields - Container for recipient form fields
 * @property {HTMLInputElement} [recipientFirstName] - Recipient first name input (optional when single name field)
 * @property {HTMLInputElement} [recipientLastName] - Recipient last name input (optional when single name field)
 * @property {HTMLInputElement} recipientName - Hidden recipient name field (combined first + last)
 * @property {HTMLInputElement} recipientEmail - Recipient email input field
 * @property {HTMLTextAreaElement} recipientMessage - Recipient message textarea
 * @property {HTMLInputElement} [timezoneOffset] - Timezone offset hidden input (optional)
 * @property {HTMLInputElement} [controlFlag] - Shopify gift card control flag (optional as it's dynamically queried)
 * @property {HTMLDivElement} [emailError] - Email error message container (optional)
 * @property {HTMLDivElement} [nameError] - Name error message container (optional)
 * @property {HTMLDivElement} [messageError]
 * @property {HTMLDivElement} [liveRegion] - Live region for screen reader announcements (optional)
 */

/**
 * @extends {Component<GiftCardRecipientFormRefs>}
 */
class GiftCardRecipientForm extends Component {
  static DeliveryMode = {
    SELF: 'self', // Send to my email
    RECIPIENT: 'recipient_form', // Send to recipient's email with form
  };

  #currentMode = GiftCardRecipientForm.DeliveryMode.SELF;

  // Store bound event handlers for cleanup
  /** @type {(() => void) | null} */
  #syncRecipientNameBound = null;
  /** @type {((event: Event) => void) | null} */
  #displayCartErrorBound = null;
  /** @type {(() => void) | null} */
  #cartAddEventBound = null;

  requiredRefs = [
    'recipientFields',
    'recipientName',
    'recipientEmail',
    'recipientMessage',
  ];

  get #alwaysRecipientMode() {
    return this.dataset.alwaysRecipient === 'true';
  }

  /**
   * Get all visible recipient input fields (for enable/disable/clear)
   * @returns {(HTMLInputElement | HTMLTextAreaElement)[]} Array of input fields
   */
  get #inputFields() {
    return [
      this.refs.recipientFirstName,
      this.refs.recipientLastName,
      this.refs.recipientEmail,
      this.refs.recipientMessage,
    ].filter(Boolean);
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializeForm();
    this.#initFloatingLabels();

    if (this.refs.recipientFirstName && this.refs.recipientLastName) {
      this.#syncRecipientNameBound = () => this.#syncRecipientName();
      this.refs.recipientFirstName.addEventListener('input', this.#syncRecipientNameBound);
      this.refs.recipientLastName.addEventListener('input', this.#syncRecipientNameBound);
      this.#syncRecipientName();
    }

    this.#displayCartErrorBound = this.#displayCartError.bind(this);
    // @ts-ignore - #displayCartErrorBound is guaranteed to be non-null here
    document.addEventListener(ThemeEvents.cartError, this.#displayCartErrorBound);

    this.#cartAddEventBound = () => this.#handleCartAdd();
    document.addEventListener(ThemeEvents.cartUpdate, this.#cartAddEventBound);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#syncRecipientNameBound && this.refs.recipientFirstName && this.refs.recipientLastName) {
      this.refs.recipientFirstName.removeEventListener('input', this.#syncRecipientNameBound);
      this.refs.recipientLastName.removeEventListener('input', this.#syncRecipientNameBound);
      this.#syncRecipientNameBound = null;
    }

    if (this.#displayCartErrorBound) {
      document.removeEventListener(ThemeEvents.cartError, this.#displayCartErrorBound);
      this.#displayCartErrorBound = null;
    }

    if (this.#cartAddEventBound) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#cartAddEventBound);
      this.#cartAddEventBound = null;
    }
  }

  /**
   * Initialize form. When data-always-recipient, recipient fields are always visible and enabled.
   */
  #initializeForm() {
    if (this.#alwaysRecipientMode) {
      this.#currentMode = GiftCardRecipientForm.DeliveryMode.RECIPIENT;
      this.refs.recipientFields.hidden = false;
      this.#enableRecipientFields();
      return;
    }

    this.#updateButtonStates(GiftCardRecipientForm.DeliveryMode.SELF);
    this.refs.recipientFields.hidden = true;
    this.#clearRecipientFields();
    this.#disableRecipientFields();
  }

  /** Sync hidden Recipient name from first + last name fields. */
  #syncRecipientName() {
    if (!this.refs.recipientName) return;
    const first = this.refs.recipientFirstName?.value?.trim() ?? '';
    const last = this.refs.recipientLastName?.value?.trim() ?? '';
    this.refs.recipientName.value = [first, last].filter(Boolean).join(' ');
  }

  /** Initialize floating labels: label is hidden until field is focused or has value. */
  #initFloatingLabels() {
    const wrappers = this.querySelectorAll('[data-float-label]');
    wrappers.forEach((wrapper) => {
      const input = wrapper.querySelector('input, textarea');
      if (!input) return;

      const update = () => {
        const active = input.value.trim() !== '' || input === document.activeElement;
        wrapper.classList.toggle('is-active', active);
      };

      input.addEventListener('focus', update);
      input.addEventListener('blur', update);
      input.addEventListener('input', update);
      update();
    });
  }

  /**
   * Handle toggle between my email and recipient email
   * @param {string} mode - Delivery mode (either 'self' or 'recipient_form')
   * @param {Event} _event - Change event (unused)
   */
  toggleRecipientForm(mode, _event) {
    // Validate mode
    if (!Object.values(GiftCardRecipientForm.DeliveryMode).includes(mode)) {
      throw new Error(
        `Invalid delivery mode: ${mode}. Must be one of: ${Object.values(GiftCardRecipientForm.DeliveryMode).join(
          ', '
        )}`
      );
    }

    if (this.#currentMode === mode) return;
    this.#currentMode = mode;

    this.#updateFormState();
  }

  /** Update form state based on current mode. */
  #updateFormState() {
    const { DeliveryMode } = GiftCardRecipientForm;
    const isRecipientMode = this.#currentMode === DeliveryMode.RECIPIENT;

    this.#updateButtonStates(this.#currentMode);

    this.refs.recipientFields.hidden = !isRecipientMode;

    if (isRecipientMode) {
      this.#enableRecipientFields();

      // Announce to screen readers
      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent =
          Theme.translations?.recipient_form_fields_visible || 'Recipient form fields are now visible';
      }

      // Focus first field for accessibility
      this.refs.recipientEmail.focus();
    } else {
      this.#clearRecipientFields();
      this.#disableRecipientFields();

      // Announce to screen readers
      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent =
          Theme.translations?.recipient_form_fields_hidden || 'Recipient form fields are now hidden';
      }
    }

    this.dispatchEvent(
      new CustomEvent('recipient:toggle', {
        detail: {
          mode: this.#currentMode,
          recipientFormVisible: isRecipientMode,
        },
        bubbles: true,
      })
    );
  }

  /**
   * Update radio button states (no-op when always-recipient or refs absent)
   * @param {string} mode - Current delivery mode
   */
  #updateButtonStates(mode) {
    if (!this.refs.myEmailButton || !this.refs.recipientEmailButton) return;

    const { DeliveryMode } = GiftCardRecipientForm;

    switch (mode) {
      case DeliveryMode.SELF:
        this.refs.myEmailButton.checked = true;
        this.refs.recipientEmailButton.checked = false;
        break;

      case DeliveryMode.RECIPIENT:
        this.refs.myEmailButton.checked = false;
        this.refs.recipientEmailButton.checked = true;
        break;

      default:
        console.warn(`Unknown delivery mode: ${mode}`);
        this.refs.myEmailButton.checked = true;
        this.refs.recipientEmailButton.checked = false;
    }
  }

  /** Clear all recipient form fields. */
  #clearRecipientFields() {
    for (const field of this.#inputFields) {
      field.value = '';
    }
    if (this.refs.recipientName) this.refs.recipientName.value = '';
    this.querySelectorAll('[data-float-label]').forEach((wrapper) => {
      const input = wrapper.querySelector('input, textarea');
      if (input && !input.value.trim()) wrapper.classList.remove('is-active');
    });
    this.#clearErrorMessages();
  }

  /** Disable recipient form fields when sending to self. */
  #disableRecipientFields() {
    for (const field of this.#inputFields) {
      field.disabled = true;
      field.removeAttribute('required');
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    // Remove control field when sending to self
    const controlFlag = this.querySelector('input[name="properties[__shopify_send_gift_card_to_recipient]"]');
    if (controlFlag) {
      controlFlag.remove();
    }

    if (this.refs.timezoneOffset) {
      this.refs.timezoneOffset.disabled = true;
      this.refs.timezoneOffset.value = '';
    }

    this.#clearErrorMessages();
  }

  /** Enable recipient form fields when sending to recipient. */
  #enableRecipientFields() {
    for (const field of this.#inputFields) {
      field.disabled = false;
    }

    // Add control field when sending to recipient
    let controlFlag = this.querySelector('input[name="properties[__shopify_send_gift_card_to_recipient]"]');
    if (!controlFlag) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[__shopify_send_gift_card_to_recipient]';
      input.value = 'on';
      this.appendChild(input);
    }

    // Enable and set timezone offset
    if (this.refs.timezoneOffset) {
      this.refs.timezoneOffset.disabled = false;
      this.refs.timezoneOffset.value = new Date().getTimezoneOffset().toString();
    }

  }

  /**
   * Handles cart error events
   * @param {CartErrorEvent} event - The cart error event
   */
  #displayCartError(event) {
    if (event.detail?.data) {
      const { message, errors, description } = event.detail.data;

      // Display the error message
      if (errors && typeof errors === 'object') {
        this.#displayErrorMessage(message || 'There was an error', errors);
      } else if (message) {
        this.#displayErrorMessage(message, description);
      }
    }
  }

  /**
   * Display error messages in the appropriate error containers
   * @param {string} title - The main error message title
   * @param {Object} body - Error details
   */
  #displayErrorMessage(title, body) {
    this.#clearErrorMessages();

    if (typeof body === 'object' && body !== null) {
      /** @type {Record<string, {inputRef: string, errorRef: string}>} */
      const fieldMap = {
        email: { inputRef: 'recipientEmail', errorRef: 'emailError' },
        name: {
          inputRef: this.refs.recipientFirstName ? 'recipientFirstName' : 'recipientName',
          errorRef: 'nameError',
        },
        message: { inputRef: 'recipientMessage', errorRef: 'messageError' },
      };

      for (const [field, errorMessages] of Object.entries(body)) {
        const fieldConfig = fieldMap[field];
        if (!fieldConfig) continue;

        const { inputRef, errorRef } = fieldConfig;
        const errorContainer = this.refs[errorRef];
        const inputElement = this.refs[inputRef];

        if (errorContainer && errorContainer instanceof HTMLElement) {
          const errorTextElement = errorContainer.querySelector('span');
          if (errorTextElement) {
            const message = Array.isArray(errorMessages) ? errorMessages.join(', ') : errorMessages;
            errorTextElement.textContent = `${message}.`;
          }

          errorContainer.classList.remove('hidden');
        }

        if (inputElement && inputElement instanceof HTMLElement) {
          // Set ARIA attributes for accessibility
          inputElement.setAttribute('aria-invalid', 'true');
          const errorId = `RecipientForm-${field}-error-${this.dataset.sectionId || 'default'}`;
          inputElement.setAttribute('aria-describedby', errorId);
        }
      }
    }

    // Announce errors to screen readers
    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent =
        title || Theme.translations?.recipient_form_error || 'There was an error with the form submission';
    }
  }

  /** Clear all error messages and reset ARIA attributes. */
  #clearErrorMessages() {
    // List of error container refs
    const errorRefs = ['emailError', 'nameError', 'messageError'];

    for (const errorRef of errorRefs) {
      const errorContainer = this.refs[errorRef];
      if (errorContainer && errorContainer instanceof HTMLElement) {
        errorContainer.classList.add('hidden');
        const errorTextElement = errorContainer.querySelector('span');
        if (errorTextElement) {
          errorTextElement.textContent = '';
        }
      }
    }

    // Remove ARIA attributes from all input fields
    for (const field of this.#inputFields) {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    // Clear live region announcement
    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = '';
    }
  }

  #handleCartAdd() {
    this.#clearErrorMessages();
  }
}

// Register the custom element
customElements.define('gift-card-recipient-form', GiftCardRecipientForm);
